'use client'

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { recordAccountActivity } from '@/services/accountLifecycle/accountLifecycle.client'
import {
  ACCOUNT_ACTIVITY_ENABLED,
  accountStatusKey,
  createActivityReporter,
} from '@/services/accountLifecycle/accountLifecycle.shared'

export default function AccountActivityListener() {
  const { currentUserId, isImpersonating } = useSessionRuntime()
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!ACCOUNT_ACTIVITY_ENABLED || !currentUserId || isImpersonating) return
    const reportActivity = createActivityReporter(recordAccountActivity)
    const refreshStatus = createActivityReporter(
      async () => {
        await queryClient.invalidateQueries({
          queryKey: accountStatusKey(currentUserId),
          exact: true,
        })
      },
      Date.now,
      15 * 60_000,
    )
    const report = async () => {
      await reportActivity()
      await refreshStatus()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') void report()
    }
    const onInteraction = (event: Event) => {
      if (event.isTrusted) onVisible()
    }
    onVisible()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', onVisible)
    document.addEventListener('pointerdown', onInteraction, {
      capture: true,
      passive: true,
    })
    document.addEventListener('keydown', onInteraction, true)
    document.addEventListener('wheel', onInteraction, {
      capture: true,
      passive: true,
    })
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onVisible)
      document.removeEventListener('pointerdown', onInteraction, true)
      document.removeEventListener('keydown', onInteraction, true)
      document.removeEventListener('wheel', onInteraction, true)
    }
  }, [currentUserId, isImpersonating, queryClient])
  return null
}
